import { useEffect, useRef, useState } from "react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { useSearchHeadlines } from "@/shared/api/newsquawk-headlines";
import NewsquawkHeadlineItem from "../newsquawk-headlines/NewsquawkHeadlineItem";
import { FirehoseItem } from "../firehose-widgets/firehose-all/components/FirehoseItem";
import { useFirehoseSources } from "../firehose-widgets/shared/hooks/useFirehoseSources";
import { searchStories } from "@/shared/api/firehose";
import type { NewsFeedItem } from "@/shared/api/firehose/models";
import { toStoriesParams } from "@/shared/api/concierge-search/toStoriesParams";
import {
  requestSearchIntent,
  searchIntentEnabled,
  DATE_RANGE_LABEL,
  type SearchIntent,
} from "@/shared/api/concierge-search";
import {
  intentToSearchConfig,
  isSearchable,
} from "@/shared/api/concierge-search/toSearchConfig";

const EXAMPLES = [
  "the 10 most recent Tesla stories",
  "any OPEC news this week",
  "Fed speakers and rate cuts",
];

/**
 * Ask for headlines in plain English.
 *
 * Two steps, deliberately visible as two: the concierge turns the sentence
 * into a search, and the ordinary headline search runs it. Nothing here is a
 * new way to fetch headlines — it reuses `useSearchHeadlines`, so pagination,
 * live SSE updates and rendering are the same as the Headlines widget's own
 * search, and only the step that builds the query is new.
 *
 * The interpretation is always on screen. A search that quietly misunderstood
 * you is worse than one that failed, because the results look plausible.
 *
 * Rendered plainly rather than virtualised: the answer is capped at the stated
 * limit (100 at the very most, per section), so there is no unbounded list to
 * virtualise, and plain rows keep the sticky section headers simple.
 */
export default function NlpSearch() {
  const [value, setValue] = useState("");
  const [intent, setIntent] = useState<SearchIntent | null>(null);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stories, setStories] = useState<NewsFeedItem[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  // Only the sources this account can actually see — isSourceAccessible is
  // applied here, and searching the full list would surface content the user
  // is not entitled to.
  const sources = useFirehoseSources();

  const {
    searchResults,
    searchHeadlines,
    clearSearchResults,
    isLoading,
  } = useSearchHeadlines();

  /**
   * The last query we actually spent a request on.
   *
   * Every run costs an LLM call against a per-user hourly allowance, and
   * pressing Enter twice on an unchanged box is the easiest way to burn it for
   * nothing. Re-running identical text is a no-op while its results are still
   * on screen.
   */
  const lastQueryRef = useRef<string | null>(null);

  // In-flight work must not outlive the widget: popped out, closed or switched
  // away from, this component unmounts with a request still open.
  useEffect(() => () => abortRef.current?.abort(), []);

  const run = async (text: string) => {
    const query = text.trim();
    if (!query || thinking) return;
    if (query === lastQueryRef.current && intent) return;

    // One search at a time — an earlier slow interpretation must not land on
    // top of a newer one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    lastQueryRef.current = query;

    setThinking(true);
    setError(null);
    try {
      const understood = await requestSearchIntent(query, controller.signal);
      if (controller.signal.aborted) return;
      setIntent(understood);

      const config = await intentToSearchConfig(understood, {
        signal: controller.signal,
      });
      /**
       * Checked explicitly because intentToSearchConfig swallows a failed
       * resolve — including an aborted one — and falls back to raw terms
       * rather than throwing. Without this, cancelling a search still ran it.
       */
      if (controller.signal.aborted) return;

      // Nothing resolved to anything searchable — say so, rather than sending
      // an empty query the wire would answer with the entire archive.
      if (!isSearchable(config)) {
        setError(
          "That didn't give me anything to search for — try naming a company, ticker or topic."
        );
        return;
      }
      searchHeadlines(config);

      /**
       * The third-party wire, run alongside rather than merged. It has no tag
       * dimension, so it matches words where the desk wire matched a resolved
       * tag — the results are genuinely less precise and are shown as their
       * own section instead of interleaved as if the ranking were comparable.
       *
       * Failure here is swallowed: the Newsquawk answer is already on screen
       * and losing it because a partner feed was down would be the worse
       * trade.
       */
      setStories([]);
      searchStories({
        ...toStoriesParams(
          config,
          sources.map((entry) => entry.source)
        ),
        signal: controller.signal,
      })
        .then((results) => {
          if (!controller.signal.aborted) setStories(results ?? []);
        })
        .catch((storyError) => {
          if (!controller.signal.aborted) {
            console.error("[nlp-search] firehose search failed:", storyError);
          }
        });
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error("[nlp-search] failed:", err);
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      // 503 and 429 carry copy written for the user; anything else does not.
      setError(
        (status === 503 || status === 429) && detail
          ? detail
          : "Couldn't work that one out — try rephrasing, or use the Headlines search."
      );
    } finally {
      setThinking(false);
    }
  };

  const reset = () => {
    abortRef.current?.abort();
    setValue("");
    setIntent(null);
    setStories([]);
    lastQueryRef.current = null;
    setError(null);
    clearSearchResults();
  };

  if (!searchIntentEnabled) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <p className="text-sm text-gray-500">
          Natural-language search isn&apos;t enabled on this environment yet.
        </p>
      </div>
    );
  }

  return (
    <div className="no-drag flex h-full flex-col overflow-hidden bg-gray-900">
      <div className="border-b border-gray-700/50 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <SparklesIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-squawk-blue" />
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run(value)}
              placeholder="Ask for headlines — e.g. the 10 most recent Tesla stories"
              className="w-full rounded border border-gray-700 bg-gray-800 py-2 pl-8 pr-3 text-sm text-gray-100 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-squawk-blue"
            />
          </div>

          {/* The Headlines search form's own Clear/Search pair, same markup —
              this is the same gesture, so it should not look like a new one. */}
          {(value || intent) && (
            <button
              type="button"
              onClick={reset}
              className="rounded px-3 py-1 text-xs font-medium text-gray-300 transition-colors hover:bg-gray-700"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={() => run(value)}
            disabled={!value.trim() || thinking}
            className="rounded bg-gray-700 px-3 py-1 text-xs font-medium text-gray-200 transition-colors hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {thinking ? "Asking…" : "Ask"}
          </button>
        </div>

        {/* Worked examples, and the fastest way in. An empty box asks the user
            to guess what this understands; three phrasings answer it. */}
        {!intent && !thinking && !error && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => {
                  setValue(example);
                  run(example);
                }}
                className="cursor-pointer rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-400 transition-colors hover:border-gray-600 hover:text-gray-200"
              >
                {example}
              </button>
            ))}
          </div>
        )}

        {thinking && (
          <p className="mt-2 text-xs text-gray-400">Working out the search…</p>
        )}

        {error && <p className="mt-2 text-xs text-amber-400">{error}</p>}

        {/* What it understood. Always shown, so a misreading is visible rather
            than arriving disguised as odd results. */}
        {intent && !thinking && !error && (
          <p className="mt-2 text-xs text-gray-400">
            <span className="text-squawk-blue">{intent.explanation}</span>
            {intent.date_range !== "any" && (
              <span> · {DATE_RANGE_LABEL[intent.date_range]}</span>
            )}
            <span> · up to {intent.limit}</span>
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {searchResults.length > 0 || stories.length > 0 ? (
          <>
            {/* Newsquawk first: it is the tag-matched half, and the reason
                someone is here rather than on a general news search. */}
            {searchResults.length > 0 && (
              <>
                <SectionLabel label="Newsquawk" count={searchResults.length} />
                {searchResults.map((headline) => (
                  <NewsquawkHeadlineItem key={headline.id} headline={headline} />
                ))}
              </>
            )}

            {stories.length > 0 && (
              <>
                <SectionLabel label="Other sources" count={stories.length} />
                {stories.map((story) => (
                  <FirehoseItem key={`${story.source}-${story.id}`} item={story} />
                ))}
              </>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center px-6 text-center">
            <p className="text-sm text-gray-500">
              {isLoading || thinking
                ? "Searching…"
                : intent
                  ? "Nothing matched that — try widening it."
                  : "Ask a question to search the Newsquawk wire."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Sticky so the boundary stays legible while scrolling a long answer — the
 * two halves are matched differently and it matters which one you are reading.
 */
function SectionLabel({ label, count }: { label: string; count: number }) {
  return (
    <div className="sticky top-0 z-10 flex items-baseline gap-2 border-b border-gray-700/50 bg-gray-900/95 px-3 py-1.5 backdrop-blur-sm">
      <span className="text-2xs font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <span className="font-mono text-2xs tabular-nums text-gray-600">
        {count}
      </span>
    </div>
  );
}
